// Contrato entre o shell e os provedores de autenticação (Parte 14.2: OidcAuthProvider real, FakeAuthProvider em dev).
import type { MeResponse } from '@giyus/api-client';

import type { AuthMode } from '../env';

export type AuthStatus = 'initializing' | 'signing-in' | 'authenticated' | 'error';

/**
 * Backend de autenticação: obtém tokens e conduz login/logout/step-up. O `/me` é lido pelo `AuthProvider`
 * através do cliente da API (bearer + `Accept-Language`), nunca pelo backend diretamente.
 */
export interface AuthBackend {
  readonly mode: AuthMode;
  /** Restaura a sessão (renovação silenciosa quando há indício de sessão). Devolve `true` quando há sessão válida. */
  initialize(): Promise<boolean>;
  /** Inicia o login (redirect PKCE). `returnTo` é a rota interna para retomar após o callback. */
  signIn(returnTo: string): Promise<void>;
  /** Conclui o callback de redirect e devolve a rota interna a retomar. */
  completeSignIn(): Promise<string>;
  signOut(): Promise<void>;
  getAccessToken(): Promise<string | null>;
  /**
   * Reautenticação com `prompt=login&acr_values=urn:giyus:acr:mfa&max_age=0` (Parte 3.5.3 / 14.2). Devolve `true`
   * quando um redirect foi iniciado (a página vai descarregar; a ação pendente precisa ser persistida) e `false`
   * quando o step-up ficou satisfeito no lugar (modo fake) — a ação pode ser repetida imediatamente.
   */
  stepUp(returnTo: string): Promise<boolean>;
  /** Avisa quando a sessão se perde por trás (renovação silenciosa falhou, token expirou): o shell volta ao login. */
  readonly onSessionLost?: (handler: () => void) => () => void;
  /** Dispara imediatamente a perda de sessão (ex.: 401 da API por sessão expirada) para retornar à tela de login. */
  readonly notifySessionLost?: () => void;
  /** `fetch` de substituição (só o backend falso responde `/me` localmente; nenhum endpoint anônimo no servidor). */
  readonly fetch?: typeof globalThis.fetch;
  /** Registra o access token emitido pelo backend C# após validação do Microsoft Entra ID (`/connect/entra`). */
  setExternalSession?: (accessToken: string, expiresInSeconds?: number) => void;
}

export interface AuthSnapshot {
  readonly status: AuthStatus;
  readonly me: MeResponse | null;
  readonly error: unknown;
}

/** Chave de sessionStorage que sobrepõe `VITE_FAKE_ME_SCENARIO` (só em modo `fake`; usada pelos E2E). */
export const FAKE_SCENARIO_STORAGE_KEY = 'giyus.fake.scenario';

/** Rota interna do callback OIDC (login e step-up). */
export const AUTH_CALLBACK_PATH = '/auth/callback';
/** Rota interna da renovação silenciosa (iframe `prompt=none`; tratada no bootstrap, fora do shell). */
export const AUTH_SILENT_CALLBACK_PATH = '/auth/silent-callback';

/** Na rota de callback quem conclui o login é a própria página (`completeSignIn`): o provedor não pode redirecionar de novo. */
export function isAuthCallbackPath(path: string): boolean {
  return path === AUTH_CALLBACK_PATH || path.startsWith(`${AUTH_CALLBACK_PATH}?`);
}
